<style lang="scss">
  @use "../../../../assets/css/design-system/utils";
  @use "../../../../assets/css/design-system/colors";

  label {
    all: unset;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;

    .padding {
      padding-bottom: utils.in_rem(12px);
    }

    .inner {
      overflow-y: auto;
      &.restrict-height {
        max-height: utils.in_rem(200px);
      }
    }
  }

  .required {
    @include colors.color(systemError_100);
  }
</style>

<script>
  // atoms
  import HelperText from "../../../atoms/InputLabels/HelperText.svelte";
  import Label from "../../../atoms/InputLabels/Label.svelte";
  import Search from "../../../atoms/Search/Search.svelte";
  // dropdown components
  import Display from "../Display.svelte";
  import Dropdown from "../Dropdown.svelte";
  import ListItem from "../ListItem.svelte";
  import NoItems from "../NoItems.svelte";
  import ToggleAll from "./ToggleAll.svelte";
  // stores and utils
  import { dropdownStores } from "./stores";
  import { findFirst, itemTransform } from "./utils";

  let cls = "";
  export { cls as class };

  // Props for the label above the dropdown
  export let label;
  export let labelSize = "small";
  export let tooltipMessage = "";

  // Props for the helper text below the dropdown
  export let helperText;
  export let state;

  // Props for the dropdown itself
  export let leadingIcon = ""; // Icon to display on the left of the dropdown
  export let placeholder = "Select"; // Text to display when no items are selected
  export let placeholderIcon = ""; // Icon to display when no items are selected
  export let showFilter = true; // Search bar at top of dropdown
  export let size = "large";
  export let dropdownStyle = "";
  export let restrictHeight = false; // Restrict height of dropdown to 200px
  export let showToggleAll = true; // Show 'Select All' toggle at top of dropdown

  // Required props -- make sure these are defined for the dropdown to be enabled
  export let items = []; // All options to choose from [{id: string | number, label: string, icon: string}, ...]
  export let selectedItems = []; // Selected options (you can bind to this)
  export let priorityItemIds = []; // Item IDs that should appear at top in order specified

  // KNOWN ISSUES:
  //
  // 1 - The binding for selectedItems is currently only one-way (i.e. it will update
  //     when the dropdown changes, but not the other way around).
  //
  //     This means when initializing the dropdown, you must ensure that selectedItems
  //     is set to the intended defaults BEFORE the dropdown is mounted (unless it is
  //     null or undefined, in which case the dropdown will wait for the array to become Truthy).
  //
  //     WORKAROUND:
  //     One workaround is to use the {#key } syntax to re-render the dropdown when
  //     selectedItems is changed externally, but this is a hotfix.
  //
  //     Another workaround is to use the exported setItem function (see below) to update
  //     the component's internal state, which prevents the need for re-rendering.
  //
  // 2 - You can't have two MultiSelectDropdowns bound to the same selectedItems array,
  //     otherwise you will get an infinite loop of the two dropdowns updating each other.
  //
  //     WORKAROUND:
  //     If you need two of the same dropdown, you need to make sure only one is rendered at a time
  //     (e.g. see campaign creation modal where this was needed).

  // Initialize stores and functions from stores.js
  const {
    chosen,
    topSet,
    allItems,
    filterDropdownSearch,
    filteredAndSortedItems,
    partial,
    allSelected,
    toggleAll,
    setItems,
  } = dropdownStores(priorityItemIds);

  // Can be used externally to change the internal state of selected items
  // thereby providing another workaround for the one-way binding issue (point 1 above)
  export { setItems };

  let focusSearch;
  const dropdownClick = () => {
    if (focusSearch) focusSearch();
    $filterDropdownSearch = "";
    topSet.set(itemTransform(selectedItems, items));
  };

  const dropdownClose = () => {
    topSet.set(itemTransform(selectedItems, items));
  };

  // Initialize some reactive variables
  let firstPass = true;
  let displayText = "";
  let displayIcon = "";
  let displaySrc = "";
  let displayTextDescription = "";
  let displayTextRequired = false;
  let overflow = 0;

  const dropdownProps = {
    style: dropdownStyle || "width: 100%",
    leadingIcon,
    kind: "input-field",
    size,
    onclick: dropdownClick,
    onclose: dropdownClose,
    // Don't allow the dropdown to be opened if selectedItems is undefined (see below)
    // This is because we haven't initialized the required stores yet
    disabled: true,
  };

  // Find the original item to preserve the correct ID type (str | num)
  const idCheck = (x, stringId) =>
    typeof x.id === "number" ? String(x.id) === stringId : x.id === stringId;

  // Only run this block if selectedItems / items are defined (otherwise we initialize with undefined)
  // Note that [] is truthy in JavaScript
  $: if (selectedItems && items) {
    dropdownProps.disabled = false;

    // Initialize chosen and topSet stores on first pass
    if (firstPass) {
      allItems.set(items);
      chosen.set(itemTransform(selectedItems, items));
      topSet.set(itemTransform(selectedItems, items));
      firstPass = false;
    }

    // Reactively update the selectedItems array
    // (this array of ids is what you can bind to)

    const nextSelectedItems = Object.entries($chosen)
      .filter(([_, selected]) => selected)
      .map(
        ([stringId]) => items.find((x) => idCheck(x, stringId))?.id || stringId
      );

    if (
      JSON.stringify(nextSelectedItems.toSorted()) !==
      JSON.stringify(selectedItems.toSorted())
    ) {
      selectedItems = nextSelectedItems;
    }

    // Determine what text to display in the dropdown
    // Either the first selected item (alphabetically) or placeholder text
    displayText = findFirst(selectedItems, items, "label", placeholder);

    // Use the same logic to find the corresponding icon
    displayIcon = findFirst(selectedItems, items, "icon", placeholderIcon);

    // Use the same logic to find the corresponding src
    displaySrc = findFirst(selectedItems, items, "src");

    displayTextDescription = findFirst(selectedItems, items, "description");

    displayTextRequired = findFirst(selectedItems, items, "required") || false;

    // If more than 1 item is selected, show a '+n' chip
    overflow = selectedItems.length - 1;
  }

  let closeDropdown;

  // Reactive declaration for the toggleAllProps (depends on allSelected and partial)
  $: toggleAllProps = {
    toggleAll: () => toggleAll(!$allSelected), // toggleAll expects a boolean
    allSelected: $allSelected,
    partial: $partial,
  };
</script>

<label class={cls} for="dropdown">
  {#if label}
    <Label {label} {labelSize} {tooltipMessage} />
  {/if}

  <Dropdown {...dropdownProps} bind:close={closeDropdown} {...$$restProps}>
    <span slot="button">
      {#if $$slots.display}
        <!-- Allow custom slot for what is shown on the button -->
        <slot name="display" />
      {:else}
        <Display
          {displayIcon}
          {displaySrc}
          {displayText}
          {displayTextDescription}
          {displayTextRequired}
          {overflow}
        />
      {/if}
    </span>

    <span slot="header" class:padding={showFilter}>
      {#if showFilter}
        <Search
          bind:value={$filterDropdownSearch}
          bind:focus={focusSearch}
          class="flex-0-0"
          style="z-index: 1"
        />
      {/if}
    </span>

    <!-- Don't show 'Select All' if the search bar is active to avoid ux confusion -->
    {#if showToggleAll && !$filterDropdownSearch}
      <ToggleAll {...toggleAllProps} />
    {/if}

    <div class="inner" class:restrict-height={restrictHeight}>
      {#each $filteredAndSortedItems as item}
        <ListItem
          icon={item.icon}
          src={item.src}
          onclick={setItems([item])}
          bind:selected={$chosen[item.id]}
          showCheckbox
          description={item.description}
        >
          {item.label}
          {#if item.required}
            <span class="required">*</span>
          {/if}
        </ListItem>
      {/each}
    </div>

    <!-- No items found from filter -->
    {#if $filteredAndSortedItems.length === 0}
      <NoItems />
    {/if}
  </Dropdown>

  {#if helperText}
    <HelperText {helperText} {state} />
  {/if}
</label>
